import React from 'react';
import { Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import {
  formatBoardPlayerLabel,
  formatBoardPlayersLine,
  formatFullBoardPlayersLine,
} from '../games/shared/playerName';
import { formatTimeControlLabel } from './formatTimeControl';
import { formatUtcDateLabel } from './formatUtcDate';
import {
  formatBoardOpeningTooltip,
  formatCompactBoardOpeningLabel,
  formatEcoOpeningLabel,
  formatNumberedOpeningSans,
  type EcoOpeningFields,
} from './openingFormat';

export type SourceGameMeta = EcoOpeningFields & {
  white?: string;
  black?: string;
  whiteElo?: number | string;
  blackElo?: number | string;
  timeControl?: string;
  timeClass?: string;
  /** PGN UTCDate from the source game (e.g. "2024.01.15"). */
  date?: string;
  result?: string;
  /** First N opening plies as SAN, when known from the list API. */
  openingSans?: string[];
};

export const hasSourceGameMeta = (
  meta: SourceGameMeta | null | undefined,
): boolean =>
  Boolean(
    meta &&
    (meta.white ||
      meta.black ||
      meta.timeControl ||
      meta.timeClass ||
      meta.date ||
      meta.result ||
      hasOpeningMeta(meta)),
  );

const hasOpeningMeta = (meta: SourceGameMeta): boolean =>
  formatEcoOpeningLabel(meta) !== '—' ||
  (meta.openingSans?.length ?? 0) > 0;

const formatOpeningLine = (meta: SourceGameMeta): string | null => {
  const compactLabel = formatCompactBoardOpeningLabel(meta);
  if (compactLabel !== '—') {
    return compactLabel;
  }
  const sansLabel = formatNumberedOpeningSans(meta.openingSans);
  return sansLabel !== '—' ? sansLabel : null;
};

type SourceGameHeaderProps = {
  meta: SourceGameMeta | null | undefined;
  variant?: 'center' | 'split' | 'stacked';
  /** Stacked variant: always render three text lines (hidden when empty) so layout height stays fixed. */
  reserveLines?: boolean;
  sx?: SxProps<Theme>;
};

const stackedLineSx = {
  fontSize: 'inherit',
  lineHeight: 1.35,
} as const;

const compactHeaderLineSx = {
  ...stackedLineSx,
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  wordBreak: 'normal',
} as const;

const stackedVariantFontSize = {
  xs: '0.875rem',
  lg: '0.9375rem',
  xl: '1rem',
  xxl: '1.0625rem',
  xxxl: '1.1875rem',
} as const;

const joinMetaParts = (parts: Array<string | null | undefined>): string | null => {
  const filtered = parts.filter((part): part is string => Boolean(part));
  return filtered.length > 0 ? filtered.join(' · ') : null;
};

type HeaderLineProps = {
  content: string | null;
  tooltip?: string | null;
  fontWeight?: number;
  color?: 'text.primary' | 'text.secondary';
  sx?: SxProps<Theme>;
  visibility?: 'visible' | 'hidden';
  textAlign?: 'center';
  variant?: 'body2';
};

const HeaderLine: React.FC<HeaderLineProps> = ({
  content,
  tooltip,
  fontWeight,
  color,
  sx,
  visibility,
  textAlign,
  variant,
}) => {
  const tooltipText = tooltip ?? content;
  const typography = (
    <Typography
      component="span"
      color={color}
      variant={variant}
      sx={{
        fontWeight,
        ...compactHeaderLineSx,
        visibility,
        textAlign,
        ...sx,
      }}
    >
      {content ?? '\u00A0'}
    </Typography>
  );

  if (!content || !tooltipText || tooltipText === content) {
    return typography;
  }

  return (
    <Tooltip title={tooltipText} enterDelay={300}>
      <span style={{ display: 'block', minWidth: 0 }}>{typography}</span>
    </Tooltip>
  );
};

/** Player names, rating, and time control from a source database game. */
export const SourceGameHeader: React.FC<SourceGameHeaderProps> = ({
  meta,
  variant = 'center',
  reserveLines = false,
  sx,
}) => {
  const theme = useTheme();
  // Trainer sidebar beside board from tablet landscape — split long player line.
  const stackPlayers = useMediaQuery(theme.breakpoints.up('md'));
  const hasPlayers = Boolean(meta?.white || meta?.black);
  const whiteLabel = hasPlayers
    ? formatBoardPlayerLabel(meta?.white, meta?.whiteElo, 'White')
    : null;
  const blackLabel = hasPlayers
    ? formatBoardPlayerLabel(meta?.black, meta?.blackElo, 'Black')
    : null;
  const playersLine = hasPlayers
    ? formatBoardPlayersLine(
        meta?.white,
        meta?.whiteElo,
        meta?.black,
        meta?.blackElo,
      )
    : null;
  const playersTooltip = hasPlayers
    ? formatFullBoardPlayersLine(
        meta?.white,
        meta?.whiteElo,
        meta?.black,
        meta?.blackElo,
      )
    : null;
  const timeControlLabel = formatTimeControlLabel(
    meta?.timeControl,
    meta?.timeClass,
  );
  const dateLabel = formatUtcDateLabel(meta?.date);
  const openingLine = meta ? formatOpeningLine(meta) : null;
  const openingTooltip =
    meta && openingLine
      ? (() => {
          const full = formatBoardOpeningTooltip(meta);
          return full !== '—' ? full : openingLine;
        })()
      : null;

  const renderStackedPlayers = () => {
    if (!hasPlayers) {
      return null;
    }
    if (stackPlayers && whiteLabel && blackLabel) {
      return (
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          <HeaderLine
            content={`${whiteLabel} vs`}
            tooltip={playersTooltip}
            fontWeight={600}
          />
          <HeaderLine
            content={blackLabel}
            tooltip={playersTooltip}
            fontWeight={600}
          />
        </Stack>
      );
    }
    return (
      <HeaderLine
        content={playersLine}
        tooltip={playersTooltip}
        fontWeight={600}
      />
    );
  };

  if (variant === 'stacked') {
    const gameDetailsLine = joinMetaParts([
      dateLabel,
      timeControlLabel,
      meta?.result,
    ]);
    const stackedLineSlots = [
      {
        content: playersLine,
        tooltip: playersTooltip,
        fontWeight: 600,
        color: 'text.primary' as const,
      },
      {
        content: gameDetailsLine,
        color: 'text.secondary' as const,
      },
      {
        content: openingLine,
        tooltip: openingTooltip,
        color: 'text.secondary' as const,
      },
    ];

    if (reserveLines) {
      return (
        <Stack spacing={0.5} sx={{ fontSize: stackedVariantFontSize, ...sx }}>
          {stackPlayers && hasPlayers ? (
            renderStackedPlayers()
          ) : (
            <HeaderLine
              content={stackedLineSlots[0].content}
              tooltip={stackedLineSlots[0].tooltip}
              fontWeight={stackedLineSlots[0].fontWeight}
              color={stackedLineSlots[0].color}
              visibility={stackedLineSlots[0].content ? 'visible' : 'hidden'}
            />
          )}
          {stackedLineSlots.slice(1).map((slot, index) => (
            <HeaderLine
              key={index + 1}
              content={slot.content}
              tooltip={slot.tooltip}
              fontWeight={slot.fontWeight}
              color={slot.color}
              visibility={slot.content ? 'visible' : 'hidden'}
            />
          ))}
        </Stack>
      );
    }

    if (!hasSourceGameMeta(meta)) {
      return null;
    }

    return (
      <Stack spacing={0.5} sx={{ fontSize: stackedVariantFontSize, ...sx }}>
        {renderStackedPlayers()}
        {gameDetailsLine && (
          <HeaderLine content={gameDetailsLine} color="text.secondary" />
        )}
        {openingLine && (
          <HeaderLine
            content={openingLine}
            tooltip={openingTooltip}
            color="text.secondary"
          />
        )}
      </Stack>
    );
  }

  if (!hasSourceGameMeta(meta)) {
    return null;
  }

  if (variant === 'split') {
    return (
      <Stack spacing={0.25} sx={{ fontSize: 14, ...sx }}>
        {hasPlayers && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            flexWrap="wrap"
            gap={0.5}
          >
            <HeaderLine content={playersLine} tooltip={playersTooltip} fontWeight={600} />
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography
                component="span"
                color="text.secondary"
                sx={{ fontSize: 'inherit' }}
              >
                {timeControlLabel ?? '—'}
              </Typography>
              {dateLabel && (
                <Typography
                  component="span"
                  color="text.secondary"
                  sx={{ fontSize: 'inherit' }}
                >
                  {dateLabel}
                </Typography>
              )}
              {meta!.result && (
                <Typography
                  component="span"
                  color="text.secondary"
                  sx={{ fontSize: 'inherit' }}
                >
                  {meta!.result}
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
        {openingLine && (
          <HeaderLine
            content={openingLine}
            tooltip={openingTooltip}
            color="text.secondary"
          />
        )}
      </Stack>
    );
  }

  const centerTimeControlLabel = timeControlLabel ?? '—';
  const centerMetaLine = hasPlayers
    ? joinMetaParts([playersLine, centerTimeControlLabel, dateLabel])
    : null;
  const centerMetaTooltip = hasPlayers
    ? joinMetaParts([playersTooltip, centerTimeControlLabel, dateLabel])
    : null;

  return (
    <Stack spacing={0.25} sx={{ mb: 1, ...sx }}>
      {centerMetaLine && (
        <HeaderLine
          content={centerMetaLine}
          tooltip={centerMetaTooltip}
          color="text.secondary"
          textAlign="center"
          variant="body2"
          sx={{ fontWeight: 600 }}
        />
      )}
      {openingLine && (
        <HeaderLine
          content={openingLine}
          tooltip={openingTooltip}
          color="text.secondary"
          textAlign="center"
          variant="body2"
        />
      )}
    </Stack>
  );
};
